import { createSlice } from '@reduxjs/toolkit';

import { mapErrorResponse } from 'utils/mappers';
import { IntegrationStatusEnum } from 'utils/enums';

import {
  SLICE_PREFIX,
  listHealthSystems,
  listNetworks,
  listUsers,
  listFacilities,
  addUser,
  getUserById,
  updateUser,
  resetUserPassword,
  reInviteUser,
  listConsults,
  fetchConsult,
  updateAdminConsult,
  linkOrder,
  unlinkOrder,
  listHl7Patients,
  getHl7PatientById,
  createHl7Patient,
  updateHl7Patient,
  listHl7Visits,
  getHl7VisitById,
  createHl7Visit,
  updateHl7Visit,
  addHl7VisitInsurance,
  listHl7Orders,
  getHl7OrderById,
  createHl7Order,
  updateHl7Order,
} from './actionCreators';

const defaultTableState = { data: [], pageCount: 0 };

const initialState = {
  systems: defaultTableState,
  system: {},
  facilities: defaultTableState,
  facility: {},
  networks: defaultTableState,
  network: {},
  users: defaultTableState,
  user: {},
  consults: defaultTableState,
  consult: {},
  hl7Patients: defaultTableState,
  hl7Patient: {},
  hl7Visits: defaultTableState,
  hl7Visit: {},
  hl7Orders: defaultTableState,
  hl7Order: {},
  error: null,
};

/* eslint-disable no-param-reassign */
const adminStateSlice = createSlice({
  name: SLICE_PREFIX,
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateConsult: (state, { payload }) => {
      state.consult = {
        ...state.consult,
        ...payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listHealthSystems.fulfilled, (state, { payload }) => {
        state.systems = payload;
        state.system = {};
      })
      .addCase(listFacilities.fulfilled, (state, { payload }) => {
        state.facilities = payload;
        state.facility = {};
      })
      .addCase(listNetworks.fulfilled, (state, { payload }) => {
        state.networks = payload;
      })
      .addCase(listUsers.fulfilled, (state, { payload }) => {
        state.users = payload;
      })
      .addCase(getUserById.fulfilled, (state, { payload }) => {
        state.user = payload;
      })
      .addCase(updateUser.fulfilled, (state, { payload }) => {
        state.user = payload;
      })
      .addCase(listConsults.fulfilled, (state, { payload }) => {
        state.consults = payload;
      })
      .addCase(fetchConsult.fulfilled, (state, { payload }) => {
        state.consult = payload;
      })
      .addCase(linkOrder.fulfilled, (state, { payload }) => {
        if (payload?.result) {
          state.consult.integrationStatus = IntegrationStatusEnum.MATCHED;
        }
      })
      .addCase(unlinkOrder.fulfilled, (state, { payload }) => {
        if (payload?.result) {
          state.consult.integrationStatus = IntegrationStatusEnum.UNMATCHED;
        }
      })
      .addCase(updateAdminConsult.fulfilled, (state, { payload }) => {
        state.consult = payload;
      })
      .addCase(listHl7Patients.fulfilled, (state, { payload }) => {
        state.hl7Patients = payload;
      })
      .addCase(getHl7PatientById.fulfilled, (state, { payload }) => {
        state.hl7Patient = payload;
      })
      .addCase(updateHl7Patient.fulfilled, (state, { payload }) => {
        state.hl7Patient = payload;
      })
      .addCase(listHl7Visits.fulfilled, (state, { payload }) => {
        state.hl7Visits = payload;
      })
      .addCase(getHl7VisitById.fulfilled, (state, { payload }) => {
        state.hl7Visit = payload;
      })
      .addCase(updateHl7Visit.fulfilled, (state, { payload }) => {
        state.hl7Visit = payload;
      })
      .addCase(addHl7VisitInsurance.fulfilled, (state) => {
        state.hl7Visit = { ...state.hl7Visit, hasInsurance: true };
      })
      .addCase(listHl7Orders.fulfilled, (state, { payload }) => {
        state.hl7Orders = payload;
      })
      .addCase(getHl7OrderById.fulfilled, (state, { payload }) => {
        state.hl7Order = payload;
      })
      .addCase(createHl7Order.fulfilled, (state, { payload }) => {
        state.hl7Order = payload;
      })
      .addCase(updateHl7Order.fulfilled, (state, { payload }) => {
        state.hl7Order = payload;
      })
      .addMatcher(
        (action) =>
          action.type.startsWith(`${SLICE_PREFIX}/`) &&
          action.type.endsWith('/rejected'),
        (state, action) => {
          state.error = mapErrorResponse(action);
        },
      );
  },
});
/* eslint-enable no-param-reassign */

const { reducer, actions } = adminStateSlice;

export { reducer as adminReducer };
export const adminActions = {
  ...actions,
  listHealthSystems,
  listNetworks,
  listUsers,
  listFacilities,
  addUser,
  getUserById,
  updateUser,
  resetUserPassword,
  reInviteUser,
  listConsults,
  fetchConsult,
  updateAdminConsult,
  linkOrder,
  unlinkOrder,
  listHl7Patients,
  getHl7PatientById,
  createHl7Patient,
  updateHl7Patient,
  listHl7Visits,
  getHl7VisitById,
  createHl7Visit,
  updateHl7Visit,
  addHl7VisitInsurance,
  listHl7Orders,
  getHl7OrderById,
  createHl7Order,
  updateHl7Order,
};

export default adminStateSlice;
